import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen pb-24">
      {/* Hero */}
      <div className="border-b border-cream-300 dark:border-navy-600">
        <div className="container mx-auto px-4 py-20 max-w-3xl">
          <Link to="/" className="inline-flex items-center text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest hover:text-gold-600 dark:hover:text-gold-400 transition mb-10">
            <svg className="w-3 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back to Officials
          </Link>
          <h1 className="text-5xl md:text-7xl font-black text-navy-900 dark:text-cream-50 tracking-tighter uppercase leading-[0.85] mb-6">
            About <span className="text-gold-500">the Project</span>
          </h1>
          <p className="text-lg text-navy-600 dark:text-cream-300 leading-relaxed max-w-2xl">
            Adopt A Coward is a civic accountability platform that aggregates publicly available records to create transparency around elected officials and public figures.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16 max-w-3xl space-y-16">
        {/* Mission */}
        <section>
          <h2 className="text-2xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight mb-6">Our Mission</h2>
          <div className="space-y-4">
            <p className="text-sm text-navy-700 dark:text-cream-200 leading-relaxed">
              Democracy only works when citizens have access to the information they need to hold their representatives accountable. Too often, critical records about voting history, campaign finances, ethics complaints, and public statements are scattered across dozens of government databases, news archives, and disclosure filings.
            </p>
            <p className="text-sm text-navy-700 dark:text-cream-200 leading-relaxed">
              We bring that information together in one place, making it easier for everyday Americans to understand what their elected officials are actually doing -- not just what they say they're doing.
            </p>
          </div>
        </section>

        {/* Data Sources */}
        <section>
          <h2 className="text-2xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight mb-6">Where Our Data Comes From</h2>
          <p className="text-sm text-navy-500 dark:text-navy-300 leading-relaxed mb-8">
            Every piece of information on this platform comes from publicly accessible records and official government databases. We do not use private data, leaked materials, or anonymous tips.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SourceCard
              title="Congress.gov API"
              description="The official database of the U.S. Congress. We pull legislator profiles, sponsored bills, committee assignments, and voting records through their public API."
              type="Federal"
            />
            <SourceCard
              title="OpenStates API"
              description="An open-source project tracking all 50 state legislatures. We use their API for state legislator profiles, bill sponsorships, and committee assignments."
              type="State"
            />
            <SourceCard
              title="Official State .Gov Websites"
              description="We scrape official governor and statewide executive websites for authoritative photos, bios, contact information, and press office updates from all 50 states."
              type="Gov"
            />
            <SourceCard
              title="LegiScan API"
              description="The leading state legislation tracking service. We use LegiScan to pull bills signed/vetoed by governors, executive orders, and detailed legislative action histories with per-bill sourcing."
              type="Legislative"
            />
            <SourceCard
              title="FEC.gov API"
              description="Federal Election Commission campaign finance data. We pull contribution totals, top donors, spending, cash on hand, and sector breakdowns for officials with federal campaign committees."
              type="Filings"
            />
            <SourceCard
              title="Wikipedia Rosters"
              description="Initial seed data for current governors, lieutenant governors, attorneys general, and secretaries of state is sourced from Wikipedia's maintained lists, then enriched with .gov and LegiScan data."
              type="Seed"
            />
            <SourceCard
              title="Public News Archives"
              description="Published news articles, investigative reports, and press coverage from established media outlets. Our AI agents search for and compile publicly reported information with inline source citations."
              type="Media"
            />
            <SourceCard
              title="Government Disclosures"
              description="Financial disclosures, ethics filings, FEC campaign finance data, and other mandatory public filings that officials are required by law to make available."
              type="Filings"
            />
            <SourceCard
              title="Official Statements"
              description="Press releases, social media posts, committee hearing transcripts, floor speeches, and other public statements made by officials in their official capacity."
              type="Public"
            />
          </div>
        </section>

        {/* How it works */}
        <section>
          <h2 className="text-2xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight mb-6">How Intelligence Gathering Works</h2>
          <div className="space-y-4">
            <p className="text-sm text-navy-700 dark:text-cream-200 leading-relaxed">
              Our platform uses AI-powered research agents to compile and analyze publicly available information. These agents operate similarly to how a human research analyst would -- they search public databases, read published news articles, cross-reference official records, and compile their findings into structured intelligence briefings.
            </p>
            <p className="text-sm text-navy-700 dark:text-cream-200 leading-relaxed">
              Every dossier is generated from public sources and includes citations so you can verify the information yourself. The agents focus on factual, verifiable information: voting records, financial disclosures, published statements, and court documents.
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-4 gap-4">
            <ProcessStep number={1} title="Data Ingestion" description="Official records are pulled from government APIs, .gov websites, and LegiScan, then enriched and cross-referenced." />
            <ProcessStep number={2} title="AI Research" description="Research agents search public records, news, and filings to compile intelligence briefings with inline source citations." />
            <ProcessStep number={3} title="Editorial Review" description="Every dossier passes through our analyst voice editor, which strips AI patterns and applies a consistent human-readable editorial voice." />
            <ProcessStep number={4} title="Accountability Scoring" description="Based on the evidence gathered, each official receives a Spine Score with a confidence level and specific red flags -- never a fabricated number." />
          </div>
        </section>

        {/* Spine Score / Jev */}
        <section>
          <h2 className="text-2xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight mb-6">How the Spine Score Is Built</h2>
          <div className="space-y-4">
            <p className="text-sm text-navy-700 dark:text-cream-200 leading-relaxed">
              The Spine Score is generated by <strong>Jev</strong>, a model built by TypeSafe that answers narrow, typed questions about evidence instead of free-form text. Rather than asking an AI to "write a score" and hoping it returns something we can trust, we ask Jev three kinds of structured questions:
            </p>
            <ul className="text-sm text-navy-700 dark:text-cream-200 leading-relaxed list-disc pl-5 space-y-2">
              <li><strong>Score</strong> -- where does this official's record fall on a defined scale, from "consistently acts against the public interest" to "exemplary public service"? Jev returns a position on that scale plus a confidence level, not just a single number.</li>
              <li><strong>Flags</strong> -- three yes/no questions checked against the evidence: documented conflict of interest, a reversed public position, and any corruption investigation or indictment. Each returns a probability, not a hard true/false, so we only surface a flag when the evidence clearly supports it.</li>
              <li><strong>Classification</strong> -- individual findings (like a specific news story or a social-media-vs-vote comparison) are separately classified as reflecting positively, negatively, or neutrally on the record, each with its own confidence.</li>
            </ul>
            <p className="text-sm text-navy-700 dark:text-cream-200 leading-relaxed">
              This matters because a score is only as good as its evidence. Older free-text approaches would sometimes return a plausible-sounding number even when almost no research existed for an official. We fixed that: if there isn't enough collected public record to ground a score -- which is common for officials who were <strong>just elected or appointed</strong> and haven't accumulated a track record yet, or for officials we simply haven't researched yet -- the platform does not generate one. You'll see "Not Enough Record Yet" instead of a number. We'd rather show nothing than show something we can't back up.
            </p>
            <p className="text-sm text-navy-700 dark:text-cream-200 leading-relaxed">
              The confidence level shown alongside every score reflects how concentrated the evidence is -- a high-confidence 30 means the record clearly and consistently supports that rating; a lower-confidence score means the evidence is thinner or more mixed. Red flags below a meaningful probability threshold are hidden entirely rather than shown as a weak, misleading signal.
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-2xl p-8">
          <h2 className="text-lg font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight mb-4">Important Disclaimer</h2>
          <div className="space-y-3">
            <p className="text-sm text-navy-500 dark:text-navy-300 leading-relaxed">
              All information presented on this platform is derived from publicly available records and sources. We make every effort to ensure accuracy but cannot guarantee that all information is complete or error-free.
            </p>
            <p className="text-sm text-navy-500 dark:text-navy-300 leading-relaxed">
              Accountability scores are generated by AI analysis (see "How the Spine Score Is Built" above) and represent an assessment based on available public evidence, with a confidence level attached. They should be considered as one data point among many when forming opinions about public officials -- not a definitive verdict.
            </p>
            <p className="text-sm text-navy-500 dark:text-navy-300 leading-relaxed">
              This platform is intended for civic engagement and public awareness. It is not affiliated with any political party, campaign, or government agency.
            </p>
          </div>
        </section>

        {/* Attribution */}
        <section className="text-center py-8">
          <img
            src="https://meidasnews.com/.image/cs_srgb%2Cq_auto:eco/MTk5NDU2MTUxOTk2MjEyNjM4/mtn__96x40.svg"
            alt="MeidasTouch Network"
            className="h-10 mx-auto mb-4 opacity-50"
          />
          <p className="text-xs text-navy-500 dark:text-navy-300">Inspired by a MeidasTouch Episode</p>
          <p className="text-[10px] text-navy-400 dark:text-navy-200 mt-2">Built for civic accountability and public transparency.</p>
        </section>
      </div>
    </div>
  );
};

function SourceCard({ title, description, type }: { title: string; description: string; type: string }) {
  const typeColors: Record<string, string> = {
    Federal: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    State: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
    Gov: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
    Legislative: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20',
    Seed: 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20',
    Media: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    Filings: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    Public: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
    Legal: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  };

  return (
    <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-5 hover:border-gold-400 dark:hover:border-gold-500 transition">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-black text-navy-900 dark:text-cream-50">{title}</h3>
        <span className={`text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${typeColors[type] || 'bg-cream-200 dark:bg-navy-700 text-navy-500 dark:text-navy-300 border-cream-300 dark:border-navy-600'}`}>{type}</span>
      </div>
      <p className="text-xs text-navy-500 dark:text-navy-300 leading-relaxed">{description}</p>
    </div>
  );
}

function ProcessStep({ number, title, description }: { number: number; title: string; description: string }) {
  return (
    <div className="relative">
      <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-5 h-full">
        <div className="w-8 h-8 rounded-full bg-navy-800 dark:bg-gold-500 flex items-center justify-center text-xs font-black text-white dark:text-navy-900 mb-3">{number}</div>
        <h3 className="text-sm font-black text-navy-900 dark:text-cream-50 mb-2">{title}</h3>
        <p className="text-xs text-navy-500 dark:text-navy-300 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

export default AboutPage;
